import { NavHeader } from '@/components/features/shared/NavHeader'
import { renderDeathEventType } from '@/components/features/shared/utils'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { UserRole } from '@/domain/auth/auth'
import { DeathData } from '@/domain/deathData/deathData'
import { formatParcelState, getParcelStateColor, ParcelContractDetail, ParcelContractEvent, ParcelContractState, ParcelState } from '@/domain/parcels/parcel'
import { useCreateNewEvent, useDeleteParcelContractEvent } from '@/hooks/api/useParcels'
import { useToast } from '@/hooks/use-toast'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import { AlertCircle, Calendar, History, Layers, MapPin, Save, Trash, User } from 'lucide-react'
import { useState } from 'react'
import { DeleteDeathDataModal } from './DeleteDeathDataModal'
import { EventPayloadModal } from './EventPayloadModal'
import { ManualEditDeathDataModal } from './ManualEditDeathDataModal'
import { OccupantItem } from './OccupantItem'

/**
 * Utility to compare two values, specifically handling Dates and Date strings,
 * and treating null/undefined/empty string as equivalent.
 */
const areValuesEqual = (a: any, b: any) => {
  if (a === b) return true;

  // Handle null/undefined vs empty string as equal
  const isEmpty = (val: any) => val === null || val === undefined || val === '';
  if (isEmpty(a) && isEmpty(b)) return true;

  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();

  // Handle potential date strings vs Date objects
  const isDateLike = (val: any) =>
    val instanceof Date || (typeof val === 'string' && val.length > 5 && !isNaN(Date.parse(val)));

  if (isDateLike(a) && isDateLike(b)) {
    try {
      const timeA = new Date(a).getTime();
      const timeB = new Date(b).getTime();
      if (isNaN(timeA) || isNaN(timeB)) return false;
      return timeA === timeB;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Gets the differences between two DeathData records, ignoring metadata fields.
 */
const getRecordDiff = (original: DeathData, updated: DeathData) => {
  const changes: { field: string; previousValue: any; newValue: any }[] = []
  Object.keys(updated).forEach(key => {
    if (['id', 'sellCode'].includes(key)) return;

    const k = key as keyof DeathData
    const newVal = updated[k];
    const prevVal = original[k];

    if (!areValuesEqual(prevVal, newVal)) {
      changes.push({ field: k, previousValue: prevVal, newValue: newVal })
    }
  })
  return changes
}

interface ParcelContractDetailCmpProps {
  detail: ParcelContractDetail
}

export interface PendingChanges {
  added: Partial<DeathData>[];
  updated: DeathData[];
  removed: DeathData[];
}

export const ParcelContractDetailCmp = ({ detail }: ParcelContractDetailCmpProps) => {
  const navigate = useNavigate()
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({ added: [], updated: [], removed: [] })

  const [selectedEvent, setSelectedEvent] = useState<ParcelContractEvent | null>(null)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<DeathData | null>(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deletingRecord, setDeletingRecord] = useState<DeathData | null>(null)

  const createNewEventMutation = useCreateNewEvent()
  const deleteEventMutation = useDeleteParcelContractEvent()
  const { hasAnyRole } = useAuthStore()
  const { toast } = useToast()
  const isSold = detail.parcelState === ParcelState.SOLD
  const isSuspended = detail.parcelContractState === ParcelContractState.SUSPENDED
  const canBeModified = !isSold && !isSuspended && hasAnyRole([UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin])

  const handleGoToParcelManagement = () => {
    const search = new URLSearchParams({
      section: detail.section,
      fila: detail.fila.toString(),
      page: '1',
      sellCode: detail.sellCode || 'all',
      expensesCode: detail.expensesCode || 'all',
    }).toString()
    navigate({ to: `${Paths.Management.Parcels}?${search}` as any })
  }

  // console.log(detail.deathData)

  // Calculate current occupants taking into account pending changes
  const baseOccupants = detail.deathData || []
  const currentOccupants = baseOccupants
    .filter(occ => !pendingChanges.removed.find(r => r.id === occ.id))
    .map(occ => {
      const updated = pendingChanges.updated.find(u => u.id === occ.id)
      return updated || occ
    })
    .concat(pendingChanges.added as DeathData[])

  const handleSavePending = (record: DeathData) => {
    if (record.id) {
      // It's an update or already added
      const isNewlyAdded = pendingChanges.added.find(a => a.id === record.id)
      if (isNewlyAdded) {
        setPendingChanges(prev => ({
          ...prev,
          added: prev.added.map(a => a.id === record.id ? { ...a, ...record } : a)
        }))
      } else {
        const original = baseOccupants.find(o => o.id === record.id)
        const diff = original ? getRecordDiff(original, record) : []

        if (diff.length > 0) {
          setPendingChanges(prev => ({
            ...prev,
            updated: [...prev.updated.filter(u => u.id !== record.id), record as DeathData]
          }))
        } else {
          // If no actual changes, remove from updated if it was there
          setPendingChanges(prev => ({
            ...prev,
            updated: prev.updated.filter(u => u.id !== record.id)
          }))
        }
      }
    } else {
      // It's a new record
      setPendingChanges(prev => ({
        ...prev,
        added: [...prev.added, { ...record, id: crypto.randomUUID() }]
      }))
    }
  }

  const handleRemoveRecord = (record: DeathData) => {
    const isNewlyAdded = pendingChanges.added.find(a => a.id === record.id)
    if (isNewlyAdded) {
      setPendingChanges(prev => ({
        ...prev,
        added: prev.added.filter(a => a.id !== record.id)
      }))
    } else {
      setPendingChanges(prev => ({
        ...prev,
        updated: prev.updated.filter(u => u.id !== record.id),
        removed: [...prev.removed, record]
      }))
    }
  }

  const handleCommitChanges = async () => {
    if (pendingChanges.added.length === 0 && pendingChanges.updated.length === 0 && pendingChanges.removed.length === 0) return

    // Calculate diff for payload
    const payload = {
      added: pendingChanges.added.map(a => {
        const cleaned: any = {};
        Object.entries(a).forEach(([key, val]) => {
          if (val !== undefined && val !== null && val !== '') {
            cleaned[key] = val;
          }
        });
        return cleaned;
      }),
      updated: pendingChanges.updated.map(u => {
        const original = baseOccupants.find(o => o.id === u.id)
        const changes = original ? getRecordDiff(original, u) : []
        return { id: u.id, name: u.name, changes }
      }).filter(u => u.changes.length > 0),
      removed: pendingChanges.removed
    }

    // Filter out updated that actually have no changes
    const actualUpdated = pendingChanges.updated.filter(u => payload.updated.find(pu => pu.id === u.id))

    if (actualUpdated.length === 0 && pendingChanges.added.length === 0 && pendingChanges.removed.length === 0) return

    await createNewEventMutation.mutateAsync({
      contractId: detail.contractId,
      parcelId: detail.parcelId,
      data: {
        payload,
        added: pendingChanges.added,
        updated: actualUpdated,
        removed: pendingChanges.removed.map(r => r.id)
      }
    })
    toast({ title: 'Éxito', description: 'Cambios guardados exitosamente' })
    setPendingChanges({ added: [], updated: [], removed: [] })
  }

  const handleDeleteEvent = async (eventId: string) => {
    if (confirm('¿Está seguro de que desea eliminar este evento?')) {
      await deleteEventMutation.mutateAsync({
        contractId: detail.contractId,
        parcelId: detail.parcelId,
        eventId
      })
      toast({ title: 'Éxito', description: 'Evento eliminado' })
    }
  }

  const hasPendingChanges = pendingChanges.added.length > 0 || pendingChanges.updated.length > 0 || pendingChanges.removed.length > 0

  const levels = [3, 2, 1] // Render from surface to deepest

  return (
    <div className="space-y-6">
      <NavHeader actions={
        <div className="flex items-center gap-2">
          <IconButton
            variant="default"
            title="Ir a gestión de parcelas"
            onClick={handleGoToParcelManagement}
          >
            <MapPin className="h-4 w-4" />
          </IconButton>
          <Badge className={getParcelStateColor(detail.parcelState, detail.parcelContractState)}>
            {formatParcelState(detail.parcelState, detail.parcelContractState)}
          </Badge>
        </div>
      }>
        <div className="flex flex-col items-start gap-1">
          <h1 className="text-2xl font-bold tracking-tight">Detalles de Parcela</h1>
          <div className="text-muted-foreground">
            Contrato: <span className="font-bold text-primary">{detail.contractNumber}</span> | Sección {detail.section} - Fila {detail.fila} - Parcela {detail.parcela}
          </div>
        </div>
      </NavHeader>

      {isSold && (
        <Alert variant="destructive" className="bg-purple-50 border-purple-200">
          <AlertCircle className="h-4 w-4 text-purple-600" />
          <AlertTitle className="text-purple-800 font-bold">Parcela Vendida, sin ocupar</AlertTitle>
          <AlertDescription className="text-purple-700">
            Este contrato de parcela se encuentra en estado <strong>Vendida</strong>. No se permiten realizar modificaciones, adiciones o eliminaciones de registros.
          </AlertDescription>
        </Alert>
      )}

      {isSuspended && (
        <Alert variant="destructive" className="bg-purple-50 border-purple-200">
          <AlertCircle className="h-4 w-4 text-purple-600" />
          <AlertTitle className="text-purple-800 font-bold">Parcela Suspendida</AlertTitle>
          <AlertDescription className="text-purple-700">
            Este contrato de parcela se encuentra en estado <strong>Suspendida</strong>. No se permiten realizar modificaciones, adiciones o eliminaciones de registros.
          </AlertDescription>
        </Alert>
      )}

      {hasPendingChanges && (
        <Alert variant="default" className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Cambios pendientes</AlertTitle>
          <AlertDescription className="text-amber-700 flex items-center justify-between">
            <span>Tiene modificaciones sin guardar. Revise los cambios y confírmelos para hacerlos efectivos.</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPendingChanges({ added: [], updated: [], removed: [] })} disabled={!canBeModified}>
                Descartar
              </Button>
              <Button size="sm" onClick={handleCommitChanges} disabled={createNewEventMutation.isPending || !canBeModified}>
                <Save className="h-4 w-4 mr-2" />
                {createNewEventMutation.isPending ? 'Guardando...' : 'Confirmar Cambios'}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-primary" />
                  Niveles de la Parcela
                </CardTitle>
                <div className="flex gap-2 items-center">
                  {canBeModified &&
                    <Button variant="outline" size="sm" onClick={() => {
                      setEditingRecord(null)
                      setIsEditModalOpen(true)
                    }}>
                      <User className="h-4 w-4 mr-2" />
                      Nuevo Ocupante
                    </Button>
                  }
                </div>
              </div>
              <CardDescription>Visualización de ocupación por niveles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {levels.map((level) => {
                const occupants = currentOccupants.filter((d: DeathData) => d.thumbLevel === level) || []
                return (
                  <div key={level} className="relative border rounded-xl overflow-hidden bg-gray-50/50">
                    <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
                      <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                        Nivel {level} {level === 3 ? '(Superficie)' : level === 1 ? '(Fondo)' : '(Medio)'}
                      </span>
                      <Badge variant="secondary">{occupants.length} Ocupantes</Badge>
                    </div>
                    <div className="p-4 space-y-3">
                      {occupants.length === 0 ? (
                        <p className="text-sm text-center text-muted-foreground py-4 italic">Nivel disponible</p>
                      ) : (
                        occupants.map((occ: DeathData) => (
                          <OccupantItem
                            key={occ.id}
                            occ={occ}
                            disabled={!canBeModified}
                            onEdit={(record) => {
                              setEditingRecord(record)
                              setIsEditModalOpen(true)
                            }}
                            onDelete={(record) => {
                              setDeletingRecord(record)
                              setIsDeleteModalOpen(true)
                            }}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-4 items-start">
            <div className="bg-blue-500 text-white p-2 rounded-lg">
              <History className="h-5 w-5" />
            </div>
            <div>
              <h4 className="font-bold text-blue-900">Reglas de Capacidad</h4>
              <p className="text-sm text-blue-800 mt-1">
                Cada nivel tiene capacidad para:
              </p>
              <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
                <li>Un ataúd</li>
                <li>O tres reducciones</li>
                <li>O seis o más urnas cinerarias</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                Historial de Eventos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative space-y-4 before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-300 before:to-transparent">
                {detail.events && detail.events?.length > 0 ? (
                  detail.events.map((event: ParcelContractEvent) => (
                    <div key={event.id} className="relative flex items-center gap-4 group is-active">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-gray-300 text-gray-800 shadow shrink-0 z-10">
                        <Calendar className="h-4 w-4" />
                      </div>
                      <div className={`flex-1 p-4 rounded border shadow relative ${event.payload ? 'bg-blue-50 border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors' : 'bg-white border-slate-200'}`}
                        onClick={() => event.payload && setSelectedEvent(event)}>
                        <div className="flex items-center justify-between space-x-2 mb-1">
                          <div className="font-bold text-slate-900">{renderDeathEventType(event.eventType)}</div>
                          {event.eventDate && <time className="font-mono text-xs text-gray-800 rounded-xl p-1 bg-gray-300 px-2 py-1">
                            {format(new Date(event.eventDate), "dd/MM/yyyy")}</time>}
                        </div>
                        <div className="text-slate-500 text-sm capitalize">
                          {event.deathName?.toLowerCase()}
                        </div>
                        {event.payload && (
                          <div className="text-xs text-blue-600 font-medium mt-2 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {(() => {
                              const { added = [], updated = [], removed = [] } = event.payload;
                              const parts = [];
                              if (added.length > 0) parts.push(`${added.length} agregados`);
                              if (updated.length > 0) parts.push(`${updated.length} modificados`);
                              if (removed.length > 0) parts.push(`${removed.length} eliminados`);
                              return parts.join(' | ') || 'Ver detalles';
                            })()}
                          </div>
                        )}
                        {event.eventType === "MANUAL_EDIT" && canBeModified && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute bottom-2 right-2 h-6 w-6 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-center text-muted-foreground py-8">No se registraron eventos para esta parcela</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {selectedEvent && (
        <EventPayloadModal
          isOpen={!!selectedEvent}
          onClose={() => setSelectedEvent(null)}
          event={selectedEvent}
        />
      )}

      {isEditModalOpen && (
        <ManualEditDeathDataModal
          isOpen={isEditModalOpen}
          onClose={() => { setIsEditModalOpen(false); setEditingRecord(null); }}
          record={editingRecord}
          onSave={handleSavePending}
        />
      )}

      {isDeleteModalOpen && deletingRecord && (
        <DeleteDeathDataModal
          isOpen={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setDeletingRecord(null); }}
          record={deletingRecord}
          onConfirm={() => { handleRemoveRecord(deletingRecord); setIsDeleteModalOpen(false); setDeletingRecord(null); }}
        />
      )}
    </div>
  )
}
